
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';

import User, { UserStatus } from '@/store/models/user';
import { UsersActions } from '@/store/modules/users/types';
import { UserRole } from '@/store/modules/user/profile/types';

import FormInput from '@/components/controls/form-input.vue';
import Modal from '@/components/layout/modal.vue';
import SubmitButton from '@/components/controls/submit-button.vue';

export default Vue.extend({
  name: 'InviteUserModal',
  inject: {
    $validator: '$validator',
  },
  components: {
    FormInput,
    Modal,
    SubmitButton,
  },
  data() {
    return {
      successMessage: '',
      errorMessage: '',
      formKey: 0,
    };
  },
  computed: {
    ...mapState('user/profile', {
      currentUser: 'currentProfile',
    }),
    ...mapGetters('user/profile', ['practiceId']),
    ...mapState('users', {
      user: 'editable',
    }),
    ...mapGetters('users', {
      isFetching: 'editableIsFetching',
      isSaving: 'editableIsSaving',
    }),
    isFormValid(): boolean {
      if (!this.user) return false;
      return this.user.firstName.trim() !== '' && 
             this.user.lastName.trim() !== '' && 
             this.user.email.trim() !== '' && 
             this.errors.count() === 0;
    },
  },
  methods: {
    ...mapActions('users', {
      updateUser: UsersActions.UPDATE_EDITABLE,
      saveUserSilent: UsersActions.SAVE_EDITABLE_SILENT,
    }),
    async initializeUser() {
      // Create a new User instance with default values for invitation
      const user = new User();
      user.firstName = '';
      user.lastName = '';
      user.email = '';
      user.role = UserRole.User;
      user.status = UserStatus.PendingInvitation;
      user.practice = {
        id: this.practiceId || this.currentUser.practiceId,
      } as any;
      user.languageCode = this.currentUser.languageCode;
      user.countries = this.currentUser.countries || [];

      // Set the editable user in the store using the mutation directly
      this.$store.commit('users/SET_EDITABLE', user);
    },
    async saveUser() {
      // Clear previous messages and validation errors
      this.successMessage = '';
      this.errorMessage = '';
      this.$validator.reset();

      try {
        // Use the silent Vuex action that doesn't show global notifications
        await this.saveUserSilent();

        // Show local success message
        this.successMessage = this.$t('A new user was added successfully.');

        // Force Vue to re-render by using $nextTick and reinitializing user
        await this.$nextTick();
        await this.initializeUser();
        
        // Force form re-render by incrementing the key
        this.formKey++;

      } catch (error: any) {
        // Handle error messages locally
        if (error && error.response && error.response.data && error.response.data.errors) {
          const errorValues = Object.keys(error.response.data.errors)
            .map((key) => error.response.data.errors[key]);
          const errorStrings = [].concat(...errorValues).filter(Boolean);
          
          if (errorStrings.length) {
            this.errorMessage = errorStrings.join('\n');
          } else {
            this.errorMessage = this.$t('An error occurred while creating the user.');
          }
        } else {
          this.errorMessage = this.$t('An error occurred while creating the user.');
        }
      }
    }
  },
  async mounted() {
    await this.initializeUser();
  },
});
